import {createReducer, on} from '@ngrx/store';
import {
  ProposalDetailPageActions, ProposalNumberActions,
  ProposalOverview,
  ProposalSelectedActions,
  ProposalTileActions,
  ProposalTransmissionActions
} from '../actions/proposal.actions';
import {ProposalTile} from '../../../model/proposal-tile';
import {Proposal} from '../../../model/proposal/proposal';
import {Offer} from '../../../model/offer/offer';
import {ProposalFilterQuery} from '../../../model/proposal-filter-query';
import {User} from '../../../model/user';
import {ProposalNumber} from '../../../model/proposal-number';
import {PrivateNote} from '../../../model/offer/private-note';

export const proposalFeatureKey = 'proposal';

export interface ProposalAppState {
  [proposalFeatureKey]: ProposalState;
}

export interface ProposalState {
  proposalTiles: ProposalTile[];
  allTilesLoaded: boolean;
  activeFilters: ProposalFilterQuery;
  proposalSelected: Proposal;
  proposalTransmissionId: string;
  proposalTransmission: Proposal;
  proposalTransmissionSecret: string;
  proposalSelectedOffers: Offer[];
  proposalSelectedUsersAllowedToView: User[];
  proposalNumber: ProposalNumber;
  selectedId: string;
  detailPageProposalTile?: ProposalTile;
  detailPageProposal?: Proposal;
  detailPageOffers?: Offer[];
  detailPageOfferSelected?: Offer;

  loading: boolean;
  loadingTiles: boolean;
  error: unknown;

  rejectionLoading: boolean;
  retractRejectionLoading: boolean;
  proposalPreview: Proposal;
  proposalTilePreview: ProposalTile;
  maxScrolled: number;
  isFormDirty: boolean;
  privateNotes: PrivateNote[];
}

export const initialState: ProposalState = {
  proposalTiles: [],
  allTilesLoaded: false,
  proposalSelected: null,
  proposalTransmissionId: null,
  proposalTransmission: null,
  proposalTransmissionSecret: null,
  proposalSelectedOffers: null,
  proposalSelectedUsersAllowedToView: [],
  proposalNumber: null,
  selectedId: null,
  activeFilters: new ProposalFilterQuery(),
  loading: false,
  loadingTiles: false,
  error: {},
  rejectionLoading: false,
  retractRejectionLoading: false,
  proposalPreview: null,
  proposalTilePreview: null,
  maxScrolled: null,
  isFormDirty: false,
  privateNotes: [],
};

export const reducer = createReducer(
  initialState,

  on(ProposalTileActions.loaded, (state, {proposalTiles}) => ({
    ...state,
    loadingTiles: false,
    proposalTiles: [...state.proposalTiles, ...proposalTiles],
    allTilesLoaded: proposalTiles.length < state.activeFilters.pageSize,
  })),

  on(ProposalTileActions.load, (state) => ({
    ...state,
    loadingTiles: true,
    activeFilters: {
      ...state.activeFilters,
      page: state.activeFilters.page == null ? 0 : state.activeFilters.page + 1
    }
  })),
  on(ProposalTileActions.rejected, (state, {rejectionOffer}) => ({
    ...state,
    proposalTiles: [...state.proposalTiles.map(proposalTile => {
      if (proposalTile.id == rejectionOffer.proposalId) {
        return {...proposalTile, hideTile: true};
      }
      return proposalTile;
    }),
    ]
  })),
  on(ProposalOverview.updateFilter, (state, {filter}) => ({
    ...state,
    loadingTiles: true,
    proposalTiles: [],
    activeFilters: {
      ...state.activeFilters,
      ...filter,
      page: 0,
      allTilesLoaded: false,
    }
  })),

  on(ProposalOverview.resetPagination, (state) => ({
    ...state,
    loadingTiles: true,
    proposalTiles: [],
    activeFilters: {
      ...state.activeFilters,
      page: null,
      allTilesLoaded: false,
    }
  })),
  on(ProposalTransmissionActions.load, (state, {id, secret}) => ({
    ...state,
    proposalTransmissionId: id,
    proposalTransmissionSecret: secret,
    loading: true,
  })),

  on(ProposalTransmissionActions.loaded, (state, {proposal}) => ({
    ...state,
    proposalTransmission: proposal,
    loading: false,
  })),

  on(ProposalSelectedActions.load, (state, {proposalId}) => ({
    ...state,
    selectedId: proposalId,
    loading: true,
  })),

  on(ProposalSelectedActions.loaded, (state, {proposalSelected}) => ({
    ...state,
    proposalSelected,
  })),

  on(ProposalSelectedActions.deselect, (state) => ({
    ...state,
    proposalSelected: null,
    selectedId: null,
  })),

  on(ProposalSelectedActions.loadedOffers, (state, {proposalSelectedOffers}) => ({
    ...state,
    loading: false,
    proposalSelectedOffers: proposalSelectedOffers,
  })),
  on(ProposalSelectedActions.loadedUserAllowedToView, (state, {usersAllowedToView}) => ({
    ...state,
    proposalSelectedUsersAllowedToView: usersAllowedToView,
  })),

  on(ProposalDetailPageActions.load, (state) => ({
    ...state,
    loading: true,
  })),

  on(ProposalDetailPageActions.loadedProposalTile, (state, {proposalTile}) => ({
    ...state,
    detailPageProposalTile: proposalTile,
  })),

  on(ProposalDetailPageActions.load, (state) => ({
    ...state,
    loading: true,
  })),

  on(ProposalDetailPageActions.loadedProposal, (state, {proposal}) => ({
    ...state,
    detailPageProposal: proposal,
    loading: false,
  })),


  on(ProposalDetailPageActions.load, (state) => ({
    ...state,
    loading: true,
  })),
  on(ProposalDetailPageActions.acceptOffer, (state) => ({
    ...state,
    loading: true,
  })),
  on(ProposalDetailPageActions.retractOffer, (state) => ({
    ...state,
    loading: true,
  })),
  on(ProposalDetailPageActions.loadedOffers, (state, {offers}) => ({
    ...state,
    detailPageOffers: offers,
  })),

  on(ProposalDetailPageActions.selectOffer, (state, {offer}) => ({
    ...state,
    detailPageOfferSelected: offer,
  })),

  on(ProposalDetailPageActions.errorLoading, (state, {error}) => ({
    ...state,
    error: error,
  })),

  on(ProposalDetailPageActions.openAll, (state) => ({
    ...state,
    detailPageListStatus: [true, true, true, true, true, true],
  })),

  on(ProposalDetailPageActions.closeAll, (state) => ({
    ...state,
    detailPageListStatus: [false, false, false, false, false, false],
  })),

  on(ProposalNumberActions.loadProposalNumber, (state) => ({
    ...state,
    loading: true,
  })),

  on(ProposalNumberActions.proposalNumberLoaded, (state, { proposalNumber }) => ({
    ...state,
    proposalNumber: proposalNumber,
    loading: false,
  })),

  on(ProposalNumberActions.proposalNumberError, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  on(ProposalTileActions.startRejectionLoading, (state) => ({
    ...state,
    rejectionLoading: true,
  })),

  on(ProposalTileActions.stopRejectionLoading, (state) => ({
    ...state,
    rejectionLoading: false,
  })),

  on(ProposalTileActions.startRetractRejectionLoading, (state) => ({
    ...state,
    retractRejectionLoading: true,
  })),

  on(ProposalTileActions.stopRetractRejectionLoading, (state) => ({
    ...state,
    retractRejectionLoading: false,
  })),

  on(ProposalDetailPageActions.proposalPreview, (state, { proposalPreview, proposalTilePreview, maxScrolled, isFormDirty }) => ({
    ...state,
    proposalPreview,
    proposalTilePreview,
    maxScrolled,
    isFormDirty
  })),

  on(ProposalDetailPageActions.resetPreview, (state) => ({
    ...state,
    proposalPreview: null,
    proposalTilePreview: null,
    maxScrolled: 0,
    isFormDirty: false
  })),

  on(ProposalDetailPageActions.loadedPrivateNotes, (state, {privateNotes}) => ({
    ...state,
    privateNotes: privateNotes,
  }))
);
